import { useEffect, useState, useSyncExternalStore } from 'react';
import type { IcasLayerKey } from './types';

type FeatureCollection = GeoJSON.FeatureCollection<GeoJSON.Geometry, Record<string, unknown>>;

type LoadState = {
  data: FeatureCollection | null;
  loading: boolean;
  error: string | null;
};

// Only entitati ships as static GeoJSON — trupuri + stands are PMTiles.
const STATIC_URLS: Partial<Record<IcasLayerKey, string>> = {
  entitati: '/data/icas/entitati.geojson',
};

// Module-level cache so a layer fetched on /app/filter doesn't re-download
// when the user opens the polygon editor.
const cache = new Map<IcasLayerKey, FeatureCollection>();

// Set of layers currently fetching. Exposed via `useIsAnyIcasLoading()` so
// a single map-mounted indicator can show the spinner regardless of which
// layer triggered the load.
const inFlight = new Set<IcasLayerKey>();
const loadListeners = new Set<() => void>();

function emitLoad(): void {
  for (const l of loadListeners) l();
}

function isAnyLoading(): boolean {
  return inFlight.size > 0;
}

function subscribeLoad(l: () => void): () => void {
  loadListeners.add(l);
  return () => {
    loadListeners.delete(l);
  };
}

export function useIsAnyIcasLoading(): boolean {
  return useSyncExternalStore(subscribeLoad, isAnyLoading, isAnyLoading);
}

/**
 * Lazy GeoJSON loader for the small ICAS layers (entitati + trupuri).
 * The fetch is deferred until `enabled` is true so users who never toggle
 * the layer never pay the bandwidth.
 *
 * Returns `data === null` while loading or disabled.
 */
export function useIcasGeoJson(layer: IcasLayerKey, enabled: boolean): LoadState {
  const [state, setState] = useState<LoadState>(() => ({
    data: cache.get(layer) ?? null,
    loading: false,
    error: null,
  }));

  useEffect(() => {
    if (!enabled) return;
    if (cache.has(layer)) {
      setState({ data: cache.get(layer)!, loading: false, error: null });
      return;
    }
    const url = STATIC_URLS[layer];
    if (!url) {
      setState({ data: null, loading: false, error: 'no-url' });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    inFlight.add(layer);
    emitLoad();
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json: FeatureCollection) => {
        if (cancelled) return;
        cache.set(layer, json);
        setState({ data: json, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          data: null,
          loading: false,
          error: err instanceof Error ? err.message : 'unknown',
        });
      })
      .finally(() => {
        inFlight.delete(layer);
        emitLoad();
      });
    return () => {
      cancelled = true;
    };
  }, [layer, enabled]);

  return state;
}
